import { useQuery } from '@tanstack/react-query';
import { useMemo, type JSX } from 'react';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { Skeleton } from '@/components/feedback';
import { listUnitAvailabilityAlertEventsForUser } from '@/lib/services/availabilityAlerts';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';

const formatTimestamp = (value: string | null): string => {
  if (value === null || value.length === 0) return '--';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(parsed));
};

const normalizeLabel = (value: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export function AvailabilityAlertsEventsPanel(): JSX.Element {
  const user = useDashboardUser();

  const eventsQuery = useQuery({
    queryKey: ['availability-alert-events'],
    queryFn: async ({ signal }) => {
      return await listUnitAvailabilityAlertEventsForUser({ user, limit: 12, signal });
    },
  });

  const events = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);

  return (
    <section className="rounded-2xl border border-surface-subtle bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-brand-primary">Recent availability events</p>
        <p className="text-xs text-text-primary/60">
          Recorded when outreach emails are enabled and unit details are saved.
        </p>
      </div>

      <div className="mt-4">
        {eventsQuery.isLoading ? (
          <div className="flex flex-col gap-2" aria-label="Loading availability events">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-12 w-full rounded-xl" />
          </div>
        ) : eventsQuery.isError ? (
          <p className="text-sm font-semibold text-rose-600">Unable to load events.</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-text-primary/70">No events recorded yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {events.map((event) => {
              const propertyCode = normalizeLabel(event.property_code) ?? 'Property';
              const unitLabel = normalizeLabel(event.unit_label ?? event.unit_code);
              const labelSuffix = unitLabel !== null ? `- ${unitLabel}` : '';
              const statusTransition =
                event.previous_status !== null
                  ? `${event.previous_status} -> ${event.new_status}`
                  : null;

              return (
                <li
                  key={event.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-surface-subtle bg-surface-subtle/30 px-4 py-3 text-sm text-text-primary"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">
                      {propertyCode} {labelSuffix}
                    </span>
                    <span className="text-xs text-text-primary/60">
                      {formatTimestamp(event.recorded_at)}
                    </span>
                  </div>
                  <StatusBadge
                    label={event.new_status}
                    tone={event.new_status === 'available' ? 'success' : 'warning'}
                    secondaryLabel={statusTransition}
                    secondaryTone="neutral"
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
